import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Footer } from "./App";

/* ============================================================
   MobileApp — "Get the mobile app" page at /mobile-app (login required).
   Android/desktop Chrome: real Install button.
   iPhone: step-by-step "Add to Home Screen" instructions.
   ============================================================ */
export default function MobileApp() {
  const [prompt, setPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const standalone = typeof window !== "undefined" && (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone);

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setPrompt(null);
  };

  return (
    <main>
      <section className="about-hero">
        <span className="mono-label">Mobile app</span>
        <h1 style={{ fontSize: "clamp(48px, 6vw, 96px)" }}>
          Fact-check from <em>your pocket.</em>
        </h1>
        <p className="lead">
          Put FactOrNot on your home screen. Check claims, scan articles, and see the same history you have here — no app store needed.
        </p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 28 }}>
          {standalone || installed ? (
            <span className="btn btn-green">✓ Installed — open it from your home screen</span>
          ) : prompt ? (
            <button type="button" className="btn btn-green" onClick={install}>Install the app →</button>
          ) : null}
          <Link to="/app" className="btn btn-line" style={{ color: "var(--ink)", borderColor: "var(--hair-strong)" }}>
            Try it in your browser
          </Link>
        </div>
      </section>

      <section className="extension-grid">
        {(isIOS || !isAndroid) && (
          <>
            <div className="extension-card">
              <span className="mono-label">iPhone · Step 1</span>
              <h3>Open in Safari</h3>
              <p>Open <strong>factornot.ai/app</strong> in <strong>Safari</strong> and log in. (Chrome on iPhone also works on iOS 16.4+.)</p>
            </div>
            <div className="extension-card">
              <span className="mono-label">iPhone · Step 2</span>
              <h3>Tap Share</h3>
              <p>Tap the <strong>Share</strong> button — the square with an arrow pointing up, at the bottom of the screen.</p>
            </div>
            <div className="extension-card">
              <span className="mono-label">iPhone · Step 3</span>
              <h3>Add to Home Screen</h3>
              <p>Scroll down and tap <strong>Add to Home Screen</strong>, then <strong>Add</strong>. The FactOrNot icon appears next to your other apps.</p>
            </div>
          </>
        )}
        {(isAndroid || !isIOS) && (
          <>
            <div className="extension-card">
              <span className="mono-label">Android · Step 1</span>
              <h3>Open in Chrome</h3>
              <p>Open <strong>factornot.ai/mobile-app</strong> in <strong>Chrome</strong> and log in.</p>
            </div>
            <div className="extension-card">
              <span className="mono-label">Android · Step 2</span>
              <h3>Tap Install</h3>
              <p>Tap <strong>Install the app</strong> above. No button? Tap Chrome’s <strong>⋮</strong> menu → <strong>Install app</strong> (or <strong>Add to Home screen</strong>).</p>
            </div>
            <div className="extension-card">
              <span className="mono-label">Android · Bonus</span>
              <h3>Scan from the Share menu</h3>
              <p>Once installed, tap <strong>Share</strong> on any article in Chrome and pick <strong>FactOrNot</strong>. The scan starts automatically.</p>
            </div>
          </>
        )}
      </section>
      <Footer />
    </main>
  );
}